import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { translateOrDefault } from "../../utils/translationUtils";
import { Project4TWSourceSettingsApi } from "./Project4TWSourceSettings.Api";
import type { Project4TWSourceSettingsViewModel } from "./Project4TWSourceSettings.ViewModel.types";
import type {
  Project4TWBucket,
  Project4TWConfiguration,
  Project4TWProject,
} from "./Project4TWSourceSettings.types";

const NONE_OPTION = "__none";
const LOAD_PROJECTS_ERROR_MESSAGE =
  "Failed to load projects. Is this a Project for the Web environment?";
const LOAD_PROJECTS_ERROR_KEY =
  "board.connections.dialog.sourceSettings.project4tw.loadProjectsError";

const toSelectedOption = (value?: string | null): string => {
  return value && value.length > 0 ? value : NONE_OPTION;
};

const mergeProjects = (
  existingProjects: Project4TWProject[],
  additionalProjects: Project4TWProject[],
): Project4TWProject[] => {
  const projectById = new Map<string, Project4TWProject>();
  [...existingProjects, ...additionalProjects].forEach((project) => {
    projectById.set(project.msdyn_projectid, project);
  });

  return Array.from(projectById.values());
};

const projectsAreEqual = (
  left: Project4TWProject[],
  right: Project4TWProject[],
): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((project, index) => {
    const matchingProject = right[index];
    return (
      matchingProject?.msdyn_projectid === project.msdyn_projectid &&
      matchingProject?.msdyn_subject === project.msdyn_subject
    );
  });
};

export const useProject4TWSourceSettingsViewModel = (
  model: SourceSettingsModel,
): Project4TWSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const translateRef = useRef(model.t);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );
  const previousEnvironmentUrlRef = useRef<string | null>(null);
  const projectsRef = useRef<Project4TWProject[]>([]);
  const shouldSeedBucketDefaultsRef = useRef(false);

  const [projects, setProjects] = useState<Project4TWProject[]>([]);
  const [buckets, setBuckets] = useState<Project4TWBucket[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [bucketsLoaded, setBucketsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    translateRef.current = model.t;
  }, [model.t]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.MSProject4TW && model.editingConnection.isAuthorised;
  const isAuthorisedProfileReady =
    isVisible && Boolean(model.editingConnection.profileName);

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<Project4TWConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const selectedProjectIds = (configuration.projects ?? []).map(
    (project) => project.msdyn_projectid,
  );
  const selectedProjectIdsKey = selectedProjectIds.join("|");
  const selectedProjectIdForBucketMode =
    selectedProjectIds.length === 1 ? selectedProjectIds[0] : "";
  const isBucketMode = selectedProjectIds.length === 1;
  const hasProjectBuckets = Boolean(configuration.projectBuckets);
  const hasProjectDoneBucket = Boolean(configuration.projectDoneBucket);

  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      previousEnvironmentUrlRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<Project4TWConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: cfg.projects ?? [],
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const currentEnvironmentUrl = configuration.url ?? "";
    if (previousEnvironmentUrlRef.current === null) {
      previousEnvironmentUrlRef.current = currentEnvironmentUrl;
      return;
    }

    if (previousEnvironmentUrlRef.current === currentEnvironmentUrl) {
      return;
    }

    previousEnvironmentUrlRef.current = currentEnvironmentUrl;

    setProjects([]);
    setBuckets([]);
    setBucketsLoaded(true);

    updateConnectionConfiguration<Project4TWConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: [],
        projectBuckets: null,
        projectDoneBucket: null,
      }),
    );
  }, [configuration.url, isVisible]);

  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setProjects([]);
    setBuckets([]);
    setBucketsLoaded(true);

    updateConnectionConfiguration<Project4TWConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: [],
        projectBuckets: null,
        projectDoneBucket: null,
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    setProjects((existingProjects) => {
      const mergedProjects = mergeProjects(existingProjects, configuration.projects ?? []);
      projectsRef.current = mergedProjects;
      return projectsAreEqual(existingProjects, mergedProjects)
        ? existingProjects
        : mergedProjects;
    });
    setProjectsLoaded(true);
  }, [selectedProjectIdsKey, configuration.projects]);
  useEffect(() => {
    const loadProjects = async (): Promise<void> => {
      if (!isAuthorisedProfileReady || !model.boardId || !editingConnectionId) {
        setProjectsLoaded(true);
        return;
      }

      const project4twApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<Project4TWSourceSettingsApi>(
          SourceTypeIds.MSProject4TW,
        );
      if (!project4twApi) {
        setProjectsLoaded(true);
        return;
      }

      setProjectsLoaded(false);
      setHasError(false);
      setErrorMessage(undefined);
      try {
        const loadedProjects = await project4twApi.getProjects(
          model.boardId,
          editingConnectionId,
        );
        const mergedProjects = mergeProjects(projectsRef.current, loadedProjects);
        projectsRef.current = mergedProjects;
        setProjects((existingProjects) =>
          projectsAreEqual(existingProjects, mergedProjects)
            ? existingProjects
            : mergedProjects,
        );
      } catch {
        setHasError(true);
        setErrorMessage(
          translateOrDefault(
            translateRef.current,
            LOAD_PROJECTS_ERROR_KEY,
            LOAD_PROJECTS_ERROR_MESSAGE,
          ),
        );
      } finally {
        setProjectsLoaded(true);
      }
    };

    void loadProjects();
  }, [editingConnectionId, isAuthorisedProfileReady, model.boardId]); 
  useEffect(() => {
    const loadProjectBuckets = async (): Promise<void> => {
      if (
        !isAuthorisedProfileReady ||
        !isBucketMode ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedProjectIdForBucketMode
      ) {
        setBuckets([]);
        setBucketsLoaded(true);
        shouldSeedBucketDefaultsRef.current = false;
        return;
      }

      const project4twApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<Project4TWSourceSettingsApi>(
          SourceTypeIds.MSProject4TW,
        );
      if (!project4twApi) {
        setBuckets([]);
        setBucketsLoaded(true);
        shouldSeedBucketDefaultsRef.current = false;
        return;
      }

      setBucketsLoaded(false);
      setHasError(false);
      setErrorMessage(undefined);
      try {
        const loadedBuckets = await project4twApi.getProjectBuckets(
          model.boardId,
          editingConnectionId,
          selectedProjectIdForBucketMode,
        );

        setBuckets(loadedBuckets);

        const shouldSeedDefaults =
          shouldSeedBucketDefaultsRef.current &&
          loadedBuckets.length > 1 &&
          (!hasProjectDoneBucket || !hasProjectBuckets);

        if (shouldSeedDefaults) {
          const defaultDoneBucket = loadedBuckets[loadedBuckets.length - 1] ?? null;
          const defaultLoadBuckets = loadedBuckets.slice(0, loadedBuckets.length - 1);

          updateConnectionConfiguration<Project4TWConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) => ({
              ...cfg,
              projectDoneBucket: cfg.projectDoneBucket ?? defaultDoneBucket,
              projectBuckets: cfg.projectBuckets ?? defaultLoadBuckets,
            }),
          );
        }
      } catch {
        setBuckets([]);
        setHasError(true);
      } finally {
        setBucketsLoaded(true);
        shouldSeedBucketDefaultsRef.current = false;
      }
    };

    void loadProjectBuckets();
  }, [
    editingConnectionId,
    isAuthorisedProfileReady,
    isBucketMode,
    model.boardId,
    selectedProjectIdForBucketMode,
    hasProjectDoneBucket,
    hasProjectBuckets,
  ]); 
  return {
    isVisible,
    isAuthorisedProfileReady,
    environmentDisplayName: configuration.orgDisplayName,
    environmentUrl: configuration.url,
    projects,
    projectsLoaded,
    buckets,
    bucketsLoaded,
    hasError,
    errorMessage,
    selectedProjectIds,
    selectedBucketIds: (configuration.projectBuckets ?? []).map(
      (bucket) => bucket.msdyn_projectbucketid,
    ),
    selectedDoneBucketId: toSelectedOption(
      configuration.projectDoneBucket?.msdyn_projectbucketid,
    ),
    isBucketMode,
    noneOptionValue: NONE_OPTION,

    onSelectProjects: (projectIds: string[]): void => {
      const selectedProjects = projectIds
        .map((projectId) =>
          projectsRef.current.find(
            (project) => project.msdyn_projectid === projectId,
          ),
        )
        .filter((project): project is Project4TWProject => Boolean(project));

      shouldSeedBucketDefaultsRef.current = selectedProjects.length === 1;
      setBuckets([]);

      updateConnectionConfiguration<Project4TWConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          projects: selectedProjects,
          projectBuckets: null,
          projectDoneBucket: null,
        }),
      );
    },

    onSelectBuckets: (bucketIds: string[]): void => {
      const selectedBuckets = bucketIds
        .map((bucketId) =>
          buckets.find((bucket) => bucket.msdyn_projectbucketid === bucketId),
        )
        .filter((bucket): bucket is Project4TWBucket => Boolean(bucket));

      updateConnectionConfiguration<Project4TWConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          projectBuckets: selectedBuckets,
        }),
      );
    },

    onSelectDoneBucket: (bucketId?: string): void => {
      const selectedDoneBucket =
        bucketId && bucketId !== NONE_OPTION
          ? (buckets.find((bucket) => bucket.msdyn_projectbucketid === bucketId) ??
            null)
          : null;

      updateConnectionConfiguration<Project4TWConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          projectDoneBucket: selectedDoneBucket,
        }),
      );
    },
  };
};
